// Inline "add food" flow for a single meal: search the trial's food database,
// pick a food, choose a portion (photographic ladder, household measure, or a
// custom gram amount), set a quantity, and save. The server re-fetches the food
// and computes nutrients, so we only send identifiers + the chosen portion.
import React, { useState } from 'react'
import { Box, Stack, Text, Button, Input, IconButton, Badge, Wrap } from '@/components/lib'
import { FaSearch, FaTimes } from 'react-icons/fa'
import api from '../../../api'
import utils from '../../../utils/utils'
import { MEAL_LABELS } from './foodDiaryShared'

function splitPortions (portions) {
  const photo = []
  const measure = []
  ;(portions || []).forEach(p => {
    if (p.image || p.image_thumbnail) photo.push(p)
    else measure.push(p)
  })
  return { photo, measure }
}

function portionKey (p) {
  return `${p.size}|${p.unit}|${p.description || ''}|${p.image || ''}`
}

export default function FoodDiaryAddFood ({ trialId, participantId, date, meal, onSaved, onCancel }) {
  const [term, setTerm] = useState('')
  const [searching, setSearching] = useState(false)
  const [results, setResults] = useState(null)
  const [food, setFood] = useState(null)
  const [portion, setPortion] = useState(null)
  const [customGrams, setCustomGrams] = useState('')
  const [quantity, setQuantity] = useState(1)
  const [saving, setSaving] = useState(false)

  function search () {
    if (term.trim().length < 2) return
    setSearching(true)
    setFood(null)
    setPortion(null)
    api.foodDiaries.searchFoods(trialId, term.trim(), { limit: 25 }, res => {
      setResults(res.results || [])
      setSearching(false)
    }, err => {
      setSearching(false)
      utils.toast('error', err.message)
    })
  }

  function selectFood (f) {
    setFood(f)
    setPortion(null)
    setCustomGrams('')
    setQuantity(1)
  }

  function save () {
    let chosen = portion
    if (!chosen && customGrams) {
      chosen = { size: parseFloat(customGrams), unit: 'grams', description: 'Custom amount' }
    }
    if (!chosen || !(chosen.size > 0)) {
      utils.toast('error', 'Choose a portion or enter a custom amount')
      return
    }
    setSaving(true)
    api.foodDiaries.createEntry(trialId, participantId, {
      date,
      meal,
      foodId: food.id,
      quantity: Number(quantity) || 1,
      portion: {
        size: chosen.size,
        unit: chosen.unit || 'grams',
        description: chosen.description || null
      }
    }, () => {
      setSaving(false)
      onSaved()
    }, err => {
      setSaving(false)
      utils.toast('error', err.message)
    })
  }

  const { photo, measure } = food ? splitPortions(food.portions) : { photo: [], measure: [] }

  return (
    <Box borderWidth='1px' borderRadius='md' p={3} bg='bg.subtle'>
      <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
        <Text fontWeight='semibold'>Add to {MEAL_LABELS[meal]}</Text>
        <IconButton aria-label='Cancel' size='xs' variant='ghost' onClick={onCancel}><FaTimes /></IconButton>
      </Stack>

      {/* Search */}
      <Stack direction='row' gap={2}>
        <Input
          size='sm'
          placeholder='Search foods (e.g. cornflakes)…'
          value={term}
          onChange={e => setTerm(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') search() }}
        />
        <Button size='sm' onClick={search} loading={searching} disabled={term.trim().length < 2}>
          <FaSearch /> Search
        </Button>
      </Stack>

      {/* Results */}
      {!food && results && (
        <Stack gap={1} mt={3} maxH='260px' overflowY='auto'>
          {results.length === 0 && <Text color='fg.muted' fontSize='sm'>No foods found.</Text>}
          {results.map(f => (
            <Box
              key={f.id}
              p={2}
              borderRadius='md'
              _hover={{ bg: 'bg.emphasized' }}
              cursor='pointer'
              onClick={() => selectFood(f)}
            >
              <Text fontSize='sm' fontWeight='medium'>{f.description}</Text>
              <Text fontSize='xs' color='fg.muted'>
                {[f.food_brand, f.food_database?.name].filter(Boolean).join(' · ')}
              </Text>
            </Box>
          ))}
        </Stack>
      )}

      {/* Portion + quantity for the chosen food */}
      {food && (
        <Stack gap={3} mt={3}>
          <Stack direction='row' justifyContent='space-between' alignItems='start'>
            <Box>
              <Text fontSize='sm' fontWeight='semibold'>{food.description}</Text>
              <Text fontSize='xs' color='fg.muted'>
                {[food.food_brand, food.food_database?.name].filter(Boolean).join(' · ')}
              </Text>
            </Box>
            <Button size='xs' variant='ghost' onClick={() => setFood(null)}>Change</Button>
          </Stack>

          {photo.length > 0 && (
            <Box>
              <Text fontSize='xs' color='fg.muted' mb={1}>Pick a portion size</Text>
              <Wrap gap={2}>
                {photo.map(p => {
                  const active = portion && portionKey(portion) === portionKey(p)
                  return (
                    <Box
                      key={portionKey(p)}
                      borderWidth='2px'
                      borderColor={active ? 'colorPalette.500' : 'border'}
                      borderRadius='md'
                      overflow='hidden'
                      cursor='pointer'
                      w='150px'
                      onClick={() => { setPortion(p); setCustomGrams('') }}
                    >
                      <img src={p.image || p.image_thumbnail} alt='' width='150' height='150' style={{ objectFit: 'cover', display: 'block', width: '150px', height: '150px' }} />
                      <Text fontSize='sm' textAlign='center' py={1} fontWeight='medium'>{p.size}{p.unit === 'grams' ? 'g' : ` ${p.unit}`}</Text>
                    </Box>
                  )
                })}
              </Wrap>
            </Box>
          )}

          {measure.length > 0 && (
            <Box>
              <Text fontSize='xs' color='fg.muted' mb={1}>Or a household measure</Text>
              <Wrap gap={2}>
                {measure.map(p => {
                  const active = portion && portionKey(portion) === portionKey(p)
                  return (
                    <Button
                      key={portionKey(p)}
                      size='xs'
                      variant={active ? 'solid' : 'outline'}
                      colorPalette={active ? 'primary' : 'gray'}
                      onClick={() => { setPortion(p); setCustomGrams('') }}
                    >
                      {p.description || `${p.size}${p.unit === 'grams' ? 'g' : ` ${p.unit}`}`} ({p.size}{p.unit === 'grams' ? 'g' : ` ${p.unit}`})
                    </Button>
                  )
                })}
              </Wrap>
            </Box>
          )}

          <Box>
            <Text fontSize='xs' color='fg.muted' mb={1}>Or a custom amount (grams)</Text>
            <Input
              size='sm'
              type='number'
              maxW='160px'
              placeholder='e.g. 45'
              value={customGrams}
              onChange={e => { setCustomGrams(e.target.value); setPortion(null) }}
            />
          </Box>

          <Stack direction='row' alignItems='center' gap={2}>
            <Text fontSize='sm'>Quantity</Text>
            <Input
              size='sm'
              type='number'
              maxW='100px'
              min={0}
              step='0.5'
              value={quantity}
              onChange={e => setQuantity(e.target.value)}
            />
            {portion && portion.size != null && (
              <Badge colorPalette='gray'>
                {+(Number(quantity || 1) * portion.size).toFixed(1)}{portion.unit === 'grams' ? 'g' : ` ${portion.unit}`} total
              </Badge>
            )}
          </Stack>

          <Stack direction='row' justifyContent='end' gap={2}>
            <Button size='sm' variant='ghost' onClick={onCancel}>Cancel</Button>
            <Button size='sm' colorPalette='primary' onClick={save} loading={saving}>Add food</Button>
          </Stack>
        </Stack>
      )}
    </Box>
  )
}
